import React, { useCallback, useMemo, useState } from "react";
import "./TradeEntryConfluenceProgressTable.css";
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

const BASE_SERVICE_URL = "/api/bot/ui/get";

function display(value) {
  return value === undefined || value === null || value === "" ? "-" : value;
}

function normalizeCsv(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .join(",");
}

function timeValue(value) {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function TradeEntryConfluenceProgressHistoryTable() {
  const { workspace } = useWorkspace();
  const [trackerIds, setTrackerIds] = useState("");
  const [symbols, setSymbols] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchRows = useCallback(() => {
    const normalizedTrackerIds = normalizeCsv(trackerIds);
    const normalizedSymbols = normalizeCsv(symbols).toUpperCase();

    if (!normalizedTrackerIds && !normalizedSymbols) {
      setRows([]);
      setError("Enter at least one tracker ID or symbol to load history.");
      return;
    }

    setLoading(true);
    setError("");

    const params = {};
    if (normalizedTrackerIds) {
      params.trackerIds = normalizedTrackerIds;
    }
    if (normalizedSymbols) {
      params.symbols = normalizedSymbols;
    }

    fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/confluenceProgressHistory`, workspace, params), {
      headers: getApiHeaders(),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => setRows(ensureArray(data)))
      .catch((err) => {
        console.error("Confluence progress history fetch failed:", err);
        setError("Unable to load confluence progress history right now.");
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [symbols, trackerIds, workspace]);

  const groupedRows = useMemo(() => {
    const groups = new Map();
    rows.forEach((row) => {
      const trackerId = display(row.tradeEntryTrackerId);
      if (!groups.has(trackerId)) {
        groups.set(trackerId, []);
      }
      groups.get(trackerId).push(row);
    });

    return Array.from(groups.entries())
      .map(([trackerId, groupRows]) => {
        const sortedRows = [...groupRows].sort((a, b) =>
          timeValue(b.progressModifiedDate || b.snapshotCreatedDate) - timeValue(a.progressModifiedDate || a.snapshotCreatedDate)
            || Number(b.id || 0) - Number(a.id || 0)
        );
        return {
          trackerId,
          rows: sortedRows,
          latestModified: sortedRows[0]?.progressModifiedDate || sortedRows[0]?.snapshotCreatedDate,
        };
      })
      .sort((a, b) => timeValue(b.latestModified) - timeValue(a.latestModified));
  }, [rows]);

  return (
    <div className="confluence-progress-container">
      <div className="confluence-progress-header">
        <div>
          <h2>Trade Entry Confluence Progress History</h2>
          <p>Snapshot history for selected confluence trackers or symbols.</p>
        </div>
        <div className="confluence-progress-actions">
          <button className="btn-add" onClick={fetchRows} disabled={loading}>{loading ? "Loading..." : "Fetch History"}</button>
          <button
            className="btn-neutral"
            onClick={() => {
              setTrackerIds("");
              setSymbols("");
              setRows([]);
              setError("");
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="confluence-history-filter-grid">
        <label>
          <span>Tracker IDs</span>
          <input
            value={trackerIds}
            placeholder="e.g. 224, 225, 247"
            onChange={(event) => setTrackerIds(event.target.value)}
          />
        </label>
        <label>
          <span>Symbols</span>
          <input
            value={symbols}
            placeholder="e.g. LINKUSDT, ETHUSDT"
            onChange={(event) => setSymbols(event.target.value)}
          />
        </label>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="confluence-progress-summary">
        Loaded {rows.length} history row{rows.length === 1 ? "" : "s"} across {groupedRows.length} tracker group{groupedRows.length === 1 ? "" : "s"}
      </div>

      {groupedRows.length === 0 && (
        <div className="empty-state">{loading ? "Loading confluence progress history..." : "No confluence progress history rows found."}</div>
      )}

      {groupedRows.map((group) => (
        <section className="confluence-history-group" key={group.trackerId}>
          <div className="confluence-history-group-header">
            <strong>Tracker ID: {group.trackerId}</strong>
            <span>{group.rows.length} row{group.rows.length === 1 ? "" : "s"}</span>
          </div>
          <div className="table-wrapper">
            <table className="attempt-log-table confluence-progress-table">
              <thead>
                <tr>
                  <th>History ID</th>
                  <th>Progress ID</th>
                  <th>Strategy ID</th>
                  <th>Rule ID</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>TF</th>
                  <th>Condition</th>
                  <th>Signal</th>
                  <th>Matched</th>
                  <th>Direction</th>
                  <th>Matched Close</th>
                  <th>Last Checked</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Signal Request</th>
                  <th>Skip Log</th>
                  <th>Progress Modified</th>
                  <th>Snapshot Created</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{display(row.id)}</td>
                    <td>{display(row.progressId)}</td>
                    <td>{display(row.strategyId)}</td>
                    <td>{display(row.ruleId)}</td>
                    <td>{display(row.symbol)}</td>
                    <td>{display(row.tradeSide)}</td>
                    <td>{display(row.timeFrame)}</td>
                    <td>{display(row.conditionType)}</td>
                    <td>{display(row.signalKey)}</td>
                    <td className={row.matched ? "matched-yes" : "matched-no"}>{row.matched ? "YES" : "NO"}</td>
                    <td>{display(row.matchedDirection)}</td>
                    <td>{display(row.matchedCandleCloseTime)}</td>
                    <td>{display(row.lastCheckedCandleCloseTime)}</td>
                    <td>{display(row.status)}</td>
                    <td className="reason-cell">{display(row.statusReason)}</td>
                    <td>{display(row.signalRequestId)}</td>
                    <td>{display(row.skipLogId)}</td>
                    <td>{display(row.progressModifiedDate)}</td>
                    <td>{display(row.snapshotCreatedDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

export default TradeEntryConfluenceProgressHistoryTable;
